import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getOnboardingState,
  markTutorialCompleted,
  dismissTutorial,
  dismissChecklist,
  resetTutorial,
  resetChecklist,
  markStepCompleted,
  markFirstLoginShown
} from '../lib/onboardingStorage';
import {
  getTourSteps,
  getChecklistItems
} from '../lib/onboardingSteps';

/**
 * Custom hook for managing onboarding state
 *
 * @param {Object} profile - User profile object
 * @param {Object} stats - User statistics (applications count, jobs count, etc.)
 * @returns {Object} Onboarding state and actions
 */
export const useOnboarding = (profile, stats = {}) => {
  const [state, setState] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [tourJustFinished, setTourJustFinished] = useState(false);

  const userId = profile?.id;

  // Detect role from profile.role field
  // Values: 'employer', 'owner' -> employer checklist
  // Values: 'job_seeker', 'contractor' -> seeker checklist
  const profileRole = (profile?.role || '').toLowerCase();
  const role = (profileRole === 'employer' || profileRole === 'owner') ? 'employer' : 'seeker';

  // Load state on mount and when userId changes
  // Tour no longer auto-starts — users can opt in via "Take Tour" button in checklist
  useEffect(() => {
    if (userId) {
      const loadedState = getOnboardingState(userId);

      setState((prev) => {
        if (prev && (prev.tutorialDismissed || prev.tutorialCompleted)) {
          return prev;
        }
        return loadedState;
      });
    }
  }, [userId]);

  // Get tour steps based on role
  const tourSteps = useMemo(() => getTourSteps(role), [role]);

  // Get checklist items based on role
  const checklistItems = useMemo(() => getChecklistItems(role), [role]);

  // Calculate completed checklist items
  const completedItems = useMemo(() => {
    if (!state || !profile) return [];

    const manuallyCompleted = state.completedSteps || [];
    const autoCompleted = [];

    checklistItems.forEach((item) => {
      // Check if auto-completion condition is met
      if (item.checkCompletion && item.checkCompletion(profile, stats)) {
        autoCompleted.push(item.id);
      }
    });

    // Combine manually completed and auto-completed, remove duplicates
    return [...new Set([...manuallyCompleted, ...autoCompleted])];
  }, [state, profile, stats, checklistItems]);

  // Calculate progress
  const progress = useMemo(() => {
    const total = checklistItems.length;
    const completed = completedItems.length;
    return {
      total,
      completed,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [checklistItems, completedItems]);

  // Check if all items are completed
  const allCompleted = useMemo(() => {
    return progress.completed === progress.total;
  }, [progress]);

  // Check if checklist should be shown
  // Hide checklist when tour is running so it doesn't block highlighted elements
  const showChecklist = useMemo(() => {
    if (!state) return false;
    if (runTour) return false; // Hide during tour
    if (state.checklistDismissed) return false;
    if (allCompleted) return false;
    return true;
  }, [state, runTour, allCompleted]);

  // Check if tour should run
  const shouldRunTour = useMemo(() => {
    if (!state) return false;
    if (state.tutorialCompleted || state.tutorialDismissed) return false;
    return runTour;
  }, [state, runTour]);

  // Handle tour completion
  const handleTourComplete = useCallback(() => {
    if (userId) {
      markTutorialCompleted(userId);
      setState((prev) => ({ ...prev, tutorialCompleted: true }));
    }
    setRunTour(false);
    setTourStepIndex(0);
    setTourJustFinished(true);
  }, [userId]);

  // Handle tour skip/dismiss
  const handleTourDismiss = useCallback(() => {
    if (userId) {
      dismissTutorial(userId);
      setState((prev) => ({ ...prev, tutorialDismissed: true }));
    }
    setRunTour(false);
    setTourStepIndex(0);
    setTourJustFinished(true);
  }, [userId]);

  // Handle checklist dismiss
  const handleChecklistDismiss = useCallback(() => {
    if (userId) {
      dismissChecklist(userId);
      setState((prev) => ({ ...prev, checklistDismissed: true }));
    }
  }, [userId]);

  // Start tour manually
  const startTour = useCallback(() => {
    if (userId) {
      resetTutorial(userId);
      setState((prev) => ({
        ...prev,
        tutorialCompleted: false,
        tutorialDismissed: false
      }));
    }
    setTourStepIndex(0);
    setRunTour(true);
  }, [userId]);

  // Show checklist again
  const showChecklistAgain = useCallback(() => {
    if (userId) {
      resetChecklist(userId);
      setState((prev) => ({ ...prev, checklistDismissed: false }));
    }
  }, [userId]);

  // Mark a checklist item as completed manually
  const completeItem = useCallback((itemId) => {
    if (userId) {
      markStepCompleted(userId, itemId);
      setState((prev) => ({
        ...prev,
        completedSteps: [...(prev?.completedSteps || []), itemId]
      }));
    }
  }, [userId]);

  // Check if an item is completed
  const isItemCompleted = useCallback((itemId) => {
    return completedItems.includes(itemId);
  }, [completedItems]);

  // Handle Joyride callback
  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Handle step changes
    if (type === 'step:after') {
      setTourStepIndex(index + 1);
    }

    // Handle tour completion
    if (status === 'finished') {
      handleTourComplete();
    }

    // Handle skip/close
    if (status === 'skipped' || action === 'close') {
      handleTourDismiss();
    }
  }, [handleTourComplete, handleTourDismiss]);

  return {
    // State
    state,
    tourSteps,
    checklistItems,
    completedItems,
    progress,
    allCompleted,
    showChecklist,
    shouldRunTour,
    tourStepIndex,
    tourJustFinished,
    role,

    // Actions
    startTour,
    handleTourComplete,
    handleTourDismiss,
    handleChecklistDismiss,
    showChecklistAgain,
    completeItem,
    isItemCompleted,
    handleJoyrideCallback,
    setRunTour
  };
};

export default useOnboarding;
